import "../input-text-base/index.css"
import "./index.css"

import type {
  CalendarDateTime,
  Time,
  ZonedDateTime,
} from "@internationalized/date"
import Icon from "@utilities/Icon"
import type { CombineAriaPropsWithCustomProps } from "@utilities/reactAriaProps"
import classNames from "classnames"
import React from "react"
import {
  DateInput,
  DateSegment,
  Label,
  Text,
  TimeField as AriaTimeField,
  type TimeFieldProps as AriaTimeFieldProps,
} from "react-aria-components"

export type TimeFieldSize = "md" | "lg"

export type TimeFieldValue = CalendarDateTime | Time | ZonedDateTime

export interface TimeFieldProps {
  /** Helper text displayed below the component. Styled as error text when `invalid` is `true`. */
  description?: string
  /** Disables the field. */
  disabled?: boolean
  /** If `true`, always shows leading zeros in the hour field. */
  forceLeadingZeros?: boolean
  /** If `true`, renders the label visually hidden. */
  hideLabel?: boolean
  /** If `true`, applies error styling to the field and description. */
  invalid?: boolean
  /** Accessible label for the field. */
  label: string
  /** The maximum allowed time (e.g. `new Time(17, 0)`). */
  max?: TimeFieldValue
  /** The minimum allowed time (e.g. `new Time(9, 0)`). */
  min?: TimeFieldValue
  /** Allows the value to be read but not changed. */
  readOnly?: boolean
  /** If `true`, appends an asterisk after the label text. */
  required?: boolean
  /** The size of the time field. */
  size?: TimeFieldSize
}

type AriaTimeFieldPropsToOmit =
  | "children"
  | "granularity"
  | "placeholderValue"
  | "shouldForceLeadingZeros"
  | "slot"

type AriaTimeFieldPropsToInclude =
  | "defaultValue"
  | "hideTimeZone"
  | "hourCycle"
  | "name"
  | "onChange"
  | "value"

export type TimeFieldElementProps = CombineAriaPropsWithCustomProps<
  AriaTimeFieldProps<TimeFieldValue>,
  TimeFieldProps,
  AriaTimeFieldPropsToOmit,
  AriaTimeFieldPropsToInclude
>

/**
 * A time field component allows users to enter and edit time values as individual
 * segments. Each segment supports keyboard entry, increment/decrement
 * with arrow keys, and respects the user's locale (12- vs 24-hour clock),
 * which can be overridden with `hourCycle`.
 *
 * Values are `Time`, `CalendarDateTime`, or `ZonedDateTime` from
 * `@internationalized/date`. Restrict the allowed range with `min`/`max`.
 *
 * @component
 * @see {@link https://planningcenter.github.io/tapestry/?path=/docs/components-timefield--docs | Storybook Documentation}
 */
export function TimeField({
  className,
  description,
  disabled,
  forceLeadingZeros,
  hideLabel,
  invalid,
  label,
  max,
  min,
  readOnly,
  required,
  size = "md",
  ...restProps
}: TimeFieldElementProps) {
  const combinedClassName = classNames(
    "tds-field tds-time-field",
    {
      "tds-field--lg": size === "lg",
      "tds-time-field--lg": size === "lg",
    },
    className
  )

  return (
    <AriaTimeField
      {...restProps}
      aria-label={hideLabel ? label : undefined}
      className={combinedClassName}
      granularity="minute"
      isDisabled={disabled}
      isInvalid={invalid}
      isReadOnly={readOnly}
      isRequired={required}
      maxValue={max ?? undefined}
      minValue={min ?? undefined}
      shouldForceLeadingZeros={forceLeadingZeros}
    >
      {!hideLabel && <Label className="tds-field-label">{label}</Label>}
      <DateInput className="tds-field-control tds-time-field-input">
        {(segment) => (
          <DateSegment
            className={
              segment.type === "literal"
                ? "tds-field-date-segment-separator"
                : "tds-field-date-segment"
            }
            segment={segment}
          />
        )}
      </DateInput>
      {description && (
        <Text
          elementType="p"
          className="tds-field-description"
          slot="description"
        >
          <Icon
            aria-hidden
            className="tds-field-description-invalid-icon"
            symbol="general#exclamation-triangle"
          />
          {description}
        </Text>
      )}
    </AriaTimeField>
  )
}
